import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntryEventPoints } from "core-integration/src/store/entries/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import { UnstyledRACButton } from "plos/src/components/buttons/UnstyledRACButton";
import Link from "plos/src/components/links/Link";
import { rotating } from "plos/src/styles/utils.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Reload from "../../../img/icons/reload.svg?react";
import { getChipName } from "../../../utils/chips";
import { ControlArrowRight } from "../../icons/Arrows";
import {
  chipStatus,
  itemLink,
  pointsPanel,
  pointsScoreboard,
  primaryHeading,
  primaryPanel,
  primaryValue,
  reloadButton,
  scoreboardPrimary,
  scoreboardSecondary,
  secondaryHeading,
  secondaryItem,
  secondaryPanel,
  secondaryValue,
  transfersValue,
} from "./pointsScoreboard.css";
import { PointsScoreboardProps } from "./types";

const PointsScoreboard = ({
  entryData,
  fetchData,
  entryId,
  eventId,
  event,
  eventRank,
}: PointsScoreboardProps) => {
  const { t } = useTranslation("points");

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<ThunkDispatch>();

  const points = useSelector((state: RootState) =>
    getEntryEventPoints(state, entryId, eventId),
  );

  const fetchDataAndFixtures = async () => {
    // Set loading to true at start
    setIsLoading(true);

    try {
      // Run both operations in parallel
      const fixturesPromise = dispatch(fetchFixtures(eventId));
      const dataPromise = fetchData();
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(resolve, 1000),
      );

      // Wait for both data operations and the minimum timeout to allow the animation to complete
      await Promise.all([fixturesPromise, dataPromise, timeoutPromise]);
    } catch (error) {
      console.error("Error loading data", error);
    } finally {
      // Set loading to false when everything is done
      setIsLoading(false);
    }
  };

  const {
    entry_history: {
      event_transfers: entryHistoryEventTransfers,
      event_transfers_cost: entryHistoryEventTransfersCost,
    },
    active_chip,
  } = entryData;

  const chipName = active_chip ? getChipName(t, active_chip) : "";

  const eventFinished = event.finished;

  return (
    <div className={pointsScoreboard}>
      <div className={scoreboardSecondary}>
        <div className={secondaryPanel}>
          <div className={secondaryItem}>
            <div className={secondaryValue}>
              {event.average_entry_score || "-"}
            </div>
            <span className={secondaryHeading}>
              {t("averagePoints", "Average Points")}
            </span>
          </div>
          <div className={secondaryItem}>
            {event.highest_scoring_entry ? (
              <Link
                to={`/entry/${event.highest_scoring_entry}/event/${eventId}`}
                className={itemLink}
              >
                <div className={secondaryValue}>
                  {event.highest_score || "-"}
                </div>
                <span className={secondaryHeading}>
                  {t("highestPoints", "Highest Points")} <ControlArrowRight />
                </span>
              </Link>
            ) : (
              <>
                <div className={secondaryValue}>-</div>
                <span className={secondaryHeading}>
                  {t("highestPoints", "Highest Points")}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={scoreboardPrimary}>
        <div className={primaryPanel}>
          <div className={pointsPanel}>
            <div className={primaryValue}>{points}</div>
            <h3 className={primaryHeading}>
              {eventFinished
                ? t("totalPoints", "Total Points")
                : t("latestPoints", "Latest Points")}
            </h3>
          </div>
          {chipName && (
            <div
              className={eventFinished ? chipStatus.default : chipStatus.live}
            >
              {chipName}
            </div>
          )}
          {!eventFinished && (
            <UnstyledRACButton
              onPress={fetchDataAndFixtures}
              className={reloadButton}
              aria-label={t("reloadPoints", "Reload Points")!}
            >
              <Reload className={isLoading ? rotating : undefined} />
            </UnstyledRACButton>
          )}
        </div>
      </div>
      <div className={scoreboardSecondary}>
        <div className={secondaryPanel}>
          <div className={secondaryItem}>
            <div className={secondaryValue}>
              {eventRank ? eventRank.toLocaleString() : "-"}
            </div>
            <span className={secondaryHeading}>{t("gwRank", "GW Rank")}</span>
          </div>
          <div className={secondaryItem}>
            <Link to={`/entry/${entryId}/transfers`} className={itemLink}>
              <div className={secondaryValue}>
                <span>{entryHistoryEventTransfers}</span>
                {entryHistoryEventTransfersCost ? (
                  <span className={transfersValue}>
                    {t(
                      "entryHistoryEventTransfersCost",
                      "-{{entryHistoryEventTransfersCost}} pts",
                      {
                        entryHistoryEventTransfersCost,
                      },
                    )}
                  </span>
                ) : (
                  ""
                )}
              </div>
              <span className={secondaryHeading}>
                {t("transfers", "Transfers")} <ControlArrowRight />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsScoreboard;
